import React, { ReactNode } from 'react';

import Link from '@digital-hig/mui/Link';
import Pagination from '@digital-hig/mui/Pagination';
import Skeleton from '@digital-hig/mui/Skeleton';
import Typography from '@digital-hig/mui/Typography';
import cn from 'classnames';

import { useBeehiveQuery } from '../../../apis/beehive/hooks';
import isLegacyId from '../../../helpers/isLegacyId';
import { ArticleResponse } from '../../../templates/article/type';
import { generateArticlePathname } from '../../../templates/article/utils';
import { ClassResponse } from '../../../templates/class/type';
import { generateClassPathname } from '../../../templates/class/utils';

export interface EntriesTableProps {
  baseURL?: string;
  items: Array<{ id: string; title?: string }>;
  totalPages?: number;
  langCode?: string;
  loading?: boolean;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  rowContentRenderer?: (item: ClassResponse | ArticleResponse) => ReactNode;
}
export function EntriesTable({
  baseURL,
  items,
  totalPages = 1,
  langCode,
  loading,
  currentPage,
  onPageChange,
  rowContentRenderer,
}: EntriesTableProps): JSX.Element {
  const regularIds = items
    .filter(key => !isLegacyId(key.id))
    .map(item => item.id);

  const legacyIds = items
    .filter(key => isLegacyId(key.id))
    .map(item => item.id);

  const { data: regularBeehiveData, isLoading: isRegularLoading } =
    useBeehiveQuery({
      baseURL,
      params: {
        'fields.id': regularIds.join(','),
        pageSize: 20,
      },
      enabled: regularIds.length > 0,
    });

  const { data: legacyBeehiveData, isLoading: isLegacyLoading } =
    useBeehiveQuery({
      baseURL,
      params: {
        'fields.legacyid': legacyIds.join(','),
      },
      enabled: legacyIds.length > 0,
    });

  const beehiveIsLoading = isRegularLoading || isLegacyLoading;

  const beehiveItemsResponse = [
    ...(regularBeehiveData?.items || []),
    ...(legacyBeehiveData?.items || []),
  ];

  const rowClassName =
    'au-px-c20 au-py-c10 au-border au-border-gray-300 au-leading-c16 au-text-c14 au-text-white hover:au-underline';
  const linkSx = {
    fontWeight: 400,
    fontSize: 'inherit',
    lineHeight: 'inherit',
    fontFamily: 'ArtifaktElement, sans-serif',
    color: 'white !important',
    textDecoration: 'none',
    '&:hover': {
      color: 'white !important',
    },
  };
  return (
    <>
      {items?.length > 0 && (
        <table className="au-w-full">
          <tbody>
            {items.map(item => {
              if (beehiveIsLoading) {
                return (
                  <tr key={item.id}>
                    <td className="au-px-c20 au-py-c10 au-border au-border-gray-300 au-leading-c16 au-text-c14 au-text-link-text">
                      <Skeleton variant="text" className="au-max-w-c200" />
                    </td>
                  </tr>
                );
              }

              const afferentBeehiveItem = beehiveItemsResponse.find(
                beehiveItem => beehiveItem.sys.id === item.id
              );

              if (!afferentBeehiveItem && item.title) {
                return (
                  <tr key={item.id}>
                    <td className={rowClassName}>
                      <Typography sx={linkSx}>{item.title}</Typography>
                    </td>
                  </tr>
                );
              }

              if (!afferentBeehiveItem) {
                return null;
              }

              let rowContent = null;
              const { fields, sys } = afferentBeehiveItem;
              const { title, slug } = fields;
              const contentType = sys.contentType.sys.id;
              let pathname = '';
              if (contentType === 'article') {
                pathname = generateArticlePathname(slug, langCode);
              } else if (contentType === 'module') {
                pathname = generateClassPathname(slug, langCode);
              }
              if (rowContentRenderer) {
                rowContent = rowContentRenderer(afferentBeehiveItem);
              } else {
                rowContent = title;
              }
              return (
                <tr key={item.id}>
                  <td className={cn(rowClassName, 'au-text-link-text')}>
                    <Link href={pathname} sx={linkSx}>
                      {rowContent}
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {onPageChange && totalPages > 1 && (
        <Pagination
          loading={loading}
          className="au-my-c30"
          sx={{
            '& .MuiPaginationItem-root': {
              color: 'white',
            },
            '& .Mui-selected': {
              color: 'black',
            },
            '& .MuiSvgIcon-root': {
              color: 'white',
            },
          }}
          page={currentPage}
          onChange={(event: unknown, newPage: number) => {
            onPageChange(newPage);
          }}
          count={totalPages}
          siblingCount={3}
        />
      )}
    </>
  );
}
